import { Skeleton } from "@/components/ui/skeleton";

export const TourCardSkeleton = () => {
    return (
        <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
            {/* Image Skeleton */}
            <Skeleton className="aspect-[4/3] w-full" />

            <div className="p-4 space-y-3">
                {/* Destination Skeleton */}
                <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-4 rounded-full" />
                    <Skeleton className="h-4 w-24" />
                </div>

                {/* Title Skeleton */}
                <div className="space-y-2">
                    <Skeleton className="h-6 w-full" />
                    <Skeleton className="h-6 w-2/3" />
                </div>

                {/* Description Skeleton */}
                <div className="space-y-1.5 pt-1">
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-11/12" />
                </div>

                {/* Meta info Skeleton */}
                <div className="flex items-center gap-4 py-2 border-y border-border/50">
                    <Skeleton className="h-4 w-12" />
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-4 w-10" />
                </div>

                {/* Footer Skeleton */}
                <div className="flex items-center justify-between pt-2">
                    <div className="space-y-1">
                        <Skeleton className="h-3 w-8" />
                        <Skeleton className="h-6 w-16" />
                    </div>
                    <Skeleton className="h-9 w-24 rounded-md" />
                </div>
            </div>
        </div>
    );
};

export const PackageCardSkeleton = () => {
    return (
        <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
            <Skeleton className="aspect-[4/3] w-full" />
            <div className="p-4 space-y-3">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-3 w-full" />
                <div className="flex items-center gap-3 pt-2">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-4 w-12" />
                </div>
                <div className="flex items-center justify-between pt-4">
                    <Skeleton className="h-8 w-16" />
                    <Skeleton className="h-9 w-28 rounded-md" />
                </div>
            </div>
        </div>
    );
};

export const TourDetailSkeleton = () => {
    return (
        <div className="container-main px-4 pt-32 pb-16 animate-pulse">
            {/* Breadcrumb Skeleton */}
            <div className="flex gap-2 mb-6">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 w-48" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Content Column */}
                <div className="lg:col-span-2 space-y-8">
                    {/* Gallery Skeleton */}
                    <div className="space-y-4">
                        <Skeleton className="aspect-video w-full rounded-2xl" />
                        <div className="grid grid-cols-4 gap-4">
                            <Skeleton className="aspect-square rounded-xl" />
                            <Skeleton className="aspect-square rounded-xl" />
                            <Skeleton className="aspect-square rounded-xl" />
                            <Skeleton className="aspect-square rounded-xl" />
                        </div>
                    </div>

                    {/* Header Skeleton */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-4">
                            <Skeleton className="h-6 w-32 rounded-full" />
                            <Skeleton className="h-6 w-24 rounded-full" />
                        </div>
                        <Skeleton className="h-12 w-3/4" />
                        <div className="flex flex-wrap gap-6 py-4 border-y border-border">
                            <Skeleton className="h-6 w-24" />
                            <Skeleton className="h-6 w-24" />
                            <Skeleton className="h-6 w-24" />
                            <Skeleton className="h-6 w-24" />
                        </div>
                    </div>

                    {/* Sections Skeletons */}
                    <div className="space-y-8 pt-4">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="space-y-4">
                                <Skeleton className="h-8 w-48" />
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-5/6" />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Sidebar Column */}
                <div className="lg:col-span-1 space-y-6">
                    {/* Pricing Table Skeleton */}
                    <div className="bg-card border border-border rounded-xl p-5 space-y-4">
                        <Skeleton className="h-10 w-full" />
                        <div className="space-y-2">
                            {[1, 2, 3, 4].map((i) => (
                                <Skeleton key={i} className="h-12 w-full" />
                            ))}
                        </div>
                    </div>

                    {/* Booking Card Skeleton */}
                    <div className="bg-card border border-border rounded-xl p-6 space-y-6">
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-64 w-full" />
                        <Skeleton className="h-12 w-full" />
                        <Skeleton className="h-14 w-full rounded-lg" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ReviewSkeleton = () => {
    return (
        <div className="bg-card border border-border rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="space-y-2">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-3 w-16" />
                    </div>
                </div>
                <Skeleton className="h-4 w-20" />
            </div>
            <div className="space-y-2">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-2/3" />
            </div>
        </div>
    );
};
